import type { IFunctionGetMetric } from '~/modules/analytics/types';

import { IMetricFormatter } from '~/modules/analytics/types';
import { detectDataV2By } from '~/modules/analytics';
import { EMetricKey } from '~/types/metric';

export const getConversionRateMetric: IFunctionGetMetric = ({ metric }) => {
  return detectDataV2By({
    data: metric,
    extractValue: (value) => {
      return value?.totalSessionsThatCompletedCheckout && value?.totalSessions
        ? value.totalSessionsThatCompletedCheckout / value?.totalSessions
        : undefined;
    },
    formatter: IMetricFormatter.PERCENTAGE_STRING,
  });
};

export const getTotalRevenueMetric: IFunctionGetMetric = ({ metric }) => {
  return detectDataV2By({
    data: metric,
    extractValue: (value) => (value?.totalRevenue ? value?.totalRevenue : undefined),
    formatter: IMetricFormatter.CURRENCY,
  });
};

export const getTotalVisitorMetric: IFunctionGetMetric = ({ metric }) => {
  return detectDataV2By({
    data: metric,
    extractValue: (value) => (value?.totalVisitors ? value?.totalVisitors : undefined),
    formatter: IMetricFormatter.NORMAL,
  });
};

export const getTotalSessionMetric: IFunctionGetMetric = ({ metric }) => {
  return detectDataV2By({
    data: metric,
    extractValue: (value) => (value?.totalSessions ? value?.totalSessions : undefined),
    formatter: IMetricFormatter.NORMAL,
  });
};

export const getTotalOrderMetric: IFunctionGetMetric = ({ metric }) => {
  return detectDataV2By({
    data: metric,
    extractValue: (value) => (value?.totalOrders ? value?.totalOrders : undefined),
    formatter: IMetricFormatter.NORMAL,
  });
};

export const getTotalPageViewMetric: IFunctionGetMetric = ({ metric }) => {
  return detectDataV2By({
    data: metric,
    extractValue: (value) => value?.totalPageViewed,
    formatter: IMetricFormatter.NORMAL,
  });
};

export const getBounceRateMetric: IFunctionGetMetric = ({ metric }) => {
  return detectDataV2By({
    data: metric,
    extractValue: (value) => {
      if (!value) return undefined;
      const ctr =
        value?.totalSessionsWithoutAction > 0 && value?.totalSessions > 0
          ? value?.totalSessionsWithoutAction / value?.totalSessions
          : 0;
      return ctr ? ctr : undefined;
    },
    formatter: IMetricFormatter.PERCENTAGE_STRING,
  });
};

/**
 * Bounce rate is a "lower is better" metric: an increase should be shown as
 * bad (gray) and a decrease as good (green), the opposite of every other
 * metric. Compared against the enum member (not a string) so renaming/removing
 * PAGE_BR is a compile error here, not a silently-broken color.
 */
export const isReversePageAnalyticMetric = (metricKey?: EMetricKey): boolean => {
  return metricKey === EMetricKey.BOUNCE_RATE;
};

export const getDefaultMetric: IFunctionGetMetric = ({ metric }) => {
  return detectDataV2By({
    data: metric,
    extractValue: (value) => {
      if (!value) return undefined;
      return 0;
    },
    formatter: IMetricFormatter.NORMAL,
  });
};

export const getLinkCtrMetric: IFunctionGetMetric = ({ metric }) => {
  return detectDataV2By({
    data: metric,
    extractValue: (value) => {
      if (!value) return undefined;
      const ctr =
        value?.totalClickedHasHref > 0 && value?.totalPageViewed > 0
          ? value?.totalClickedHasHref / value?.totalPageViewed
          : 0;
      return ctr ? ctr : undefined;
    },
    formatter: IMetricFormatter.PERCENTAGE_STRING,
  });
};

export const getAverageTimeOnPageMetric: IFunctionGetMetric = ({ metric }) => {
  return detectDataV2By({
    data: metric,
    extractValue: (value) => {
      if (!value) return undefined;
      const averageTimeOnPage =
        value?.totalTimeOnPage > 0 && value?.totalPageViewedHaveTimeOnPage > 0
          ? value?.totalTimeOnPage / value?.totalPageViewedHaveTimeOnPage
          : 0;
      return averageTimeOnPage ? Number(averageTimeOnPage.toFixed(2)) : undefined;
    },
    formatter: IMetricFormatter.DURATION,
  });
};

export const getRevenuePerVisitorMetric: IFunctionGetMetric = ({ metric }) => {
  return detectDataV2By({
    data: metric,
    extractValue: (value) => {
      if (!value) return undefined;
      const revenuePerVisitor =
        value?.totalRevenue > 0 && value?.totalVisitors > 0
          ? value?.totalRevenue / value?.totalVisitors
          : 0;
      return revenuePerVisitor ? Number(revenuePerVisitor.toFixed(2)) : undefined;
    },
    formatter: IMetricFormatter.CURRENCY,
  });
};

export const getTotalSessionWithCartAdditionMetric: IFunctionGetMetric = ({ metric }) => {
  return detectDataV2By({
    data: metric,
    extractValue: (value) => value?.totalSessionsWithCartAdditions,
    formatter: IMetricFormatter.NORMAL,
  });
};

export const getTotalATCMetric: IFunctionGetMetric = ({ metric }) => {
  return detectDataV2By({
    data: metric,
    extractValue: (value) => {
      if (!value) return undefined;

      return value?.totalAddedToCart ? value?.totalAddedToCart : undefined;
    },
    formatter: IMetricFormatter.NORMAL,
  });
};

export const getAddToCartRateMetric: IFunctionGetMetric = ({ metric }) => {
  return detectDataV2By({
    data: metric,
    extractValue: (value) => {
      if (!value) return undefined;
      const addToCartRate =
        value?.totalSessionsWithCartAdditions > 0 && value?.totalSessions > 0
          ? value?.totalSessionsWithCartAdditions / value?.totalSessions
          : 0;
      return addToCartRate ? addToCartRate : undefined;
    },
    formatter: IMetricFormatter.PERCENTAGE_STRING,
  });
};

export const getTotalSessionCompletedCheckoutMetric: IFunctionGetMetric = ({ metric }) => {
  return detectDataV2By({
    data: metric,
    extractValue: (value) => {
      if (!value) return undefined;

      return value?.totalSessionsThatCompletedCheckout
        ? value?.totalSessionsThatCompletedCheckout
        : undefined;
    },
    formatter: IMetricFormatter.NORMAL,
  });
};

export const getTotalSessionReachedCheckoutMetric: IFunctionGetMetric = ({ metric }) => {
  return detectDataV2By({
    data: metric,
    extractValue: (value) => {
      if (!value) return undefined;

      return value?.totalSessionsThatReachedCheckout
        ? value?.totalSessionsThatReachedCheckout
        : undefined;
    },
    formatter: IMetricFormatter.NORMAL,
  });
};

export const getAverageOrderValueMetric: IFunctionGetMetric = ({ metric }) => {
  return detectDataV2By({
    data: metric,
    extractValue: (value) => {
      if (!value) return undefined;
      const averageOrderValue = value?.totalRevenue / value?.totalOrders;
      return averageOrderValue ? Number(averageOrderValue.toFixed(2)) : undefined;
    },
    formatter: IMetricFormatter.CURRENCY,
  });
};

export const getPathAnalysisMetric: IFunctionGetMetric = ({ metric }) => {
  return detectDataV2By({
    data: metric,
    extractValue: (value) => {
      if (!value) return undefined;
      return undefined;
    },
    formatter: IMetricFormatter.NORMAL,
  });
};

export const getTotalSessionByDeviceMetric: IFunctionGetMetric = ({ metric }) => {
  return detectDataV2By({
    data: metric,
    extractValue: (value) => {
      if (!value) return undefined;
      return undefined;
    },
    formatter: IMetricFormatter.NORMAL,
  });
};

export const getNewReturningCustomerMetric: IFunctionGetMetric = ({ metric }) => {
  return detectDataV2By({
    data: metric,
    extractValue: (value) => {
      if (!value) return undefined;
      return undefined;
    },
    formatter: IMetricFormatter.NORMAL,
  });
};

/**
 * @deprecated
 */
export const getTotalCollectionViews: IFunctionGetMetric = ({ metric }) => {
  return detectDataV2By({
    data: metric,
    extractValue: (value) => (value?.totalPageViewed ? value?.totalPageViewed : undefined),
    formatter: IMetricFormatter.NORMAL,
  });
};
/**
 * @deprecated
 */
export const getTotalProductViewMetric: IFunctionGetMetric = ({ metric }) => {
  return detectDataV2By({
    data: metric,
    extractValue: (value) => (value?.totalPageViewed ? value?.totalPageViewed : undefined),
    formatter: IMetricFormatter.NORMAL,
  });
};
